/**
 * In-product help & guide content.
 *
 * This is the single source of truth for the contextual help system. Topics are
 * surfaced in the help side-drawer, opened either from the header "Help" button
 * (lands on the index) or from inline {@link HelpButton} triggers placed next to
 * features (deep-links straight to the relevant topic).
 *
 * Keep copy here — not inline in components — so docs stay consistent and easy to
 * update in one place.
 *
 * @module lib/help/content
 */

/**
 * A single renderable block within a help topic. The drawer renders these
 * generically so content authors never touch JSX.
 */
export type HelpBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'heading'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'steps'; items: string[] }
  | { type: 'callout'; variant: 'tip' | 'warning' | 'info'; title?: string; text: string };

/** A help topic shown in the drawer. */
export interface HelpTopic {
  /** Stable id used by {@link HelpButton} and deep links. */
  id: string;
  /** Short title shown in the index and drawer header. */
  title: string;
  /** One-line description shown in the index list. */
  summary: string;
  /** Optional extra search keywords (titles/summaries/blocks are already searched). */
  keywords?: string[];
  /** Ids of related topics shown at the bottom of the topic. */
  related?: HelpTopicId[];
  /** Ordered content blocks. */
  blocks: HelpBlock[];
}

/**
 * Canonical topic ids. Using a union keeps {@link HelpButton} usages honest —
 * a typo'd topic id is a type error.
 */
export type HelpTopicId =
  | 'getting-started'
  | 'training-from-gmail'
  | 'dashboard'
  | 'messages'
  | 'rules'
  | 'folders'
  | 'settings'
  | 'best-practices'
  | 'faq';

export const HELP_TOPICS: HelpTopic[] = [
  {
    id: 'getting-started',
    title: 'How Email Ferret works',
    summary: 'The big picture: scoring, labeling, and routing your inbox.',
    keywords: ['overview', 'intro', 'start', 'basics', 'how it works'],
    related: ['training-from-gmail', 'messages', 'best-practices'],
    blocks: [
      {
        type: 'paragraph',
        text: 'Email Ferret watches your inbox in real time and scores every incoming email for signs of AI-generated cold outreach, sales pitches, and other unwanted mail. Each email gets a score; anything above your threshold is flagged.',
      },
      {
        type: 'heading',
        text: 'What happens to a flagged email',
      },
      {
        type: 'list',
        items: [
          'It gets your spam label applied (the "Ignore" label by default) so it is out of your way but never deleted.',
          'It shows up on the Messages page with its score and the reasons it was flagged.',
          'Legitimate mail can be sorted into folders (Important, Billing, Updates, and more) so your inbox stays organized.',
        ],
      },
      {
        type: 'heading',
        text: 'You stay in control',
      },
      {
        type: 'paragraph',
        text: 'Nothing is permanent. You can teach Email Ferret what belongs and what does not — either from the dashboard (Rules) or, more easily, right inside Gmail by adding or removing the spam label.',
      },
      {
        type: 'callout',
        variant: 'tip',
        title: 'Fastest way to learn the product',
        text: 'Read the "Train Email Ferret from Gmail" topic next — it is the single most powerful thing to understand.',
      },
    ],
  },
  {
    id: 'training-from-gmail',
    title: 'Train Email Ferret from Gmail',
    summary: 'Add or remove the spam label in Gmail to auto-create rules.',
    keywords: [
      'ignore',
      'label',
      'train',
      'teach',
      'allowlist',
      'blocklist',
      'feedback',
      'learn',
      'unlabel',
      'mark as spam',
      'not spam',
    ],
    related: ['rules', 'messages', 'settings'],
    blocks: [
      {
        type: 'paragraph',
        text: 'You do not need to open the dashboard to train your filter. Just use the spam label (the "Ignore" label by default) in Gmail like you already label email — Email Ferret turns each action into a rule automatically.',
      },
      {
        type: 'heading',
        text: 'Add the label → block the sender',
      },
      {
        type: 'paragraph',
        text: 'See spam we missed? Add the "Ignore" label to it in Gmail. Email Ferret creates a blocklist rule for that sender and re-scores their other emails so they get cleaned up too.',
      },
      {
        type: 'heading',
        text: 'Remove the label → trust the sender',
      },
      {
        type: 'paragraph',
        text: 'Did we flag something by mistake? Remove the "Ignore" label in Gmail. Email Ferret adds that sender to your allowlist and routes the email to its proper folder, so mail from them is never flagged again.',
      },
      {
        type: 'callout',
        variant: 'warning',
        title: 'Rules match the exact sender, not the whole company',
        text: 'Labeling blocks bob@acme.com — not jane@acme.com. To block or allow an entire domain, add a pattern like *@acme.com on the Rules page.',
      },
      {
        type: 'heading',
        text: 'Good to know',
      },
      {
        type: 'list',
        items: [
          'Removing the label only "undoes" emails that Email Ferret flagged — removing a label you added yourself does nothing.',
          'Rules are per Gmail account, not shared across the accounts you connect.',
          'We never create duplicate rules, so re-labeling the same sender is safe.',
          'Both behaviors can be turned off per account under Settings → Spam Classification.',
        ],
      },
    ],
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    summary: 'Your stats and connected Gmail accounts at a glance.',
    keywords: ['stats', 'overview', 'accounts', 'connect', 'home'],
    related: ['getting-started', 'settings'],
    blocks: [
      {
        type: 'paragraph',
        text: 'The dashboard is your home base. The stat cards summarize what Email Ferret has been doing, and the accounts section is where you connect and manage Gmail accounts.',
      },
      {
        type: 'heading',
        text: 'Stat cards',
      },
      {
        type: 'list',
        items: [
          'Emails processed recently — activity in the last 24 hours.',
          'Emails processed this month — your running monthly total.',
          'Email management — how many suspicious emails we have detected.',
          'Subscription tier — your current plan or trial status.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Click a card to jump to the Messages page filtered to the relevant emails.',
      },
      {
        type: 'heading',
        text: 'Gmail accounts',
      },
      {
        type: 'list',
        items: [
          'Reconnect — re-authorize an account if its connection expires.',
          'Set as primary — choose the default account for the dashboard.',
          'Connect another Gmail account — add more inboxes (paid plans).',
        ],
      },
    ],
  },
  {
    id: 'messages',
    title: 'Messages',
    summary: 'Review flagged emails, scores, and why they were flagged.',
    keywords: ['flagged', 'score', 'traits', 'reasons', 'search', 'spam', 'log'],
    related: ['training-from-gmail', 'rules', 'settings'],
    blocks: [
      {
        type: 'paragraph',
        text: 'The Messages page lists every email Email Ferret has flagged, newest first. Use it to review decisions and correct anything that looks wrong.',
      },
      {
        type: 'heading',
        text: 'Reading a message',
      },
      {
        type: 'list',
        items: [
          'Score — higher means more likely to be unwanted. Anything above your threshold is flagged.',
          'Reasons — the signals that contributed to the score (e.g. cold outreach phrasing, no prior contact).',
          'Click a message to open the detail view with a full trait-by-trait breakdown.',
        ],
      },
      {
        type: 'heading',
        text: 'Correcting a decision',
      },
      {
        type: 'list',
        items: [
          'Add to allowlist — trust this sender or domain so they are never flagged.',
          'Add to blocklist — always flag this sender or domain.',
          'Route to folder — send future mail from this sender to a specific folder.',
        ],
      },
      {
        type: 'callout',
        variant: 'tip',
        title: 'You can also fix things straight from Gmail',
        text: 'Removing the "Ignore" label on a flagged email allowlists the sender automatically — no need to open this page. See "Train Email Ferret from Gmail".',
      },
      {
        type: 'heading',
        text: 'Filters',
      },
      {
        type: 'list',
        items: [
          'Search by sender, domain, or reason.',
          'Toggle "Show spam messages" to include mail already in the spam folder.',
          'Filter by folder to focus on one category.',
        ],
      },
    ],
  },
  {
    id: 'rules',
    title: 'Rules: allowlist, blocklist & routing',
    summary: 'Pattern matching, domain wildcards, and where mail goes.',
    keywords: [
      'allowlist',
      'blocklist',
      'routing',
      'pattern',
      'wildcard',
      'domain',
      'whitelist',
      'blacklist',
    ],
    related: ['training-from-gmail', 'folders', 'messages'],
    blocks: [
      {
        type: 'paragraph',
        text: 'Rules are the precise controls behind the scenes. Most are created automatically when you label email in Gmail, but you can add, review, and remove them here at any time.',
      },
      {
        type: 'heading',
        text: 'Allowlist',
      },
      {
        type: 'paragraph',
        text: 'Senders and domains on the allowlist are always trusted and never flagged. Use it for clients, colleagues, and services you rely on.',
      },
      {
        type: 'heading',
        text: 'Blocklist',
      },
      {
        type: 'paragraph',
        text: 'Senders and domains on the blocklist are always flagged, regardless of score. Use it for persistent offenders.',
      },
      {
        type: 'heading',
        text: 'Pattern syntax',
      },
      {
        type: 'list',
        items: [
          'An exact address: bob@acme.com — matches only that sender.',
          'A whole domain: *@acme.com — matches everyone at that company.',
          'Patterns are normalized automatically (for example down to the top-level domain).',
        ],
      },
      {
        type: 'callout',
        variant: 'info',
        title: 'Allowlist wins ties',
        text: 'If a sender somehow matches both lists, the allowlist takes precedence and the email is trusted.',
      },
      {
        type: 'heading',
        text: 'Routing',
      },
      {
        type: 'paragraph',
        text: 'Routing rules send mail from a given sender or domain to a specific folder (standard or custom), so recurring senders always land where you expect.',
      },
    ],
  },
  {
    id: 'folders',
    title: 'Folders & routing',
    summary: 'Standard and custom folders, AI context, and back-filling history.',
    keywords: [
      'folders',
      'routing',
      'custom',
      'context',
      'historical',
      'classify',
      'important',
      'billing',
      'updates',
    ],
    related: ['rules', 'settings'],
    blocks: [
      {
        type: 'paragraph',
        text: 'Folders keep legitimate mail organized. Email Ferret can sort incoming email into categories so your inbox shows only what matters.',
      },
      {
        type: 'heading',
        text: 'Standard folders',
      },
      {
        type: 'paragraph',
        text: 'Enable any of the built-in categories — Important, Updates, Calendar, Recruiting, Billing, Finance, Socials, Notetakers, News, Shopping, and Promotions. Each enabled folder becomes a numbered Gmail label so they group neatly in your sidebar.',
      },
      {
        type: 'list',
        items: [
          'Enable/disable a folder to control whether mail is routed there.',
          'Auto-mark as read — keep a folder tidy by marking routed mail as read.',
          'Context — give the AI a sentence or two about what belongs in this folder for your specific inbox.',
        ],
      },
      {
        type: 'heading',
        text: 'Custom folders',
      },
      {
        type: 'paragraph',
        text: 'Create your own categories with a name, a description, and either strict rules or AI-based routing for anything the standard folders do not cover.',
      },
      {
        type: 'heading',
        text: 'Classify historical email',
      },
      {
        type: 'paragraph',
        text: 'Apply your folder setup to past email by choosing a number of days to look back and running a classification job. You can watch its progress and cancel it at any time.',
      },
      {
        type: 'callout',
        variant: 'tip',
        title: 'Start small',
        text: 'Enable a few folders that match how you already think about your inbox, then add custom folders once you see how routing behaves.',
      },
    ],
  },
  {
    id: 'settings',
    title: 'Settings',
    summary: 'Threshold, spam label, auto-rules, and task creation.',
    keywords: ['threshold', 'sensitivity', 'label name', 'ignore', 'auto', 'tasks', 'google tasks'],
    related: ['training-from-gmail', 'getting-started'],
    blocks: [
      {
        type: 'heading',
        text: 'Threshold',
      },
      {
        type: 'paragraph',
        text: 'The threshold (1–10) sets how aggressive filtering is. Emails scoring above it are flagged. Lower it to catch more, raise it to flag only the most obvious cases. The default is a balanced 3.',
      },
      {
        type: 'heading',
        text: 'Spam label name',
      },
      {
        type: 'paragraph',
        text: 'This is the Gmail label applied to flagged email — "Ignore" by default. You can rename it, but remember: adding this label to an email in Gmail blocklists the sender, and removing it allowlists the sender. It is a control, not just a folder.',
      },
      {
        type: 'heading',
        text: 'Auto-rules from labeling',
      },
      {
        type: 'list',
        items: [
          'Auto-block on label added — when you add the spam label in Gmail, block that sender. On by default.',
          'Auto-allow on label removed — when you remove the spam label from something we flagged, trust that sender. On by default.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Turn either off if you prefer to manage rules manually on the Rules page.',
      },
      {
        type: 'heading',
        text: 'Google Tasks',
      },
      {
        type: 'paragraph',
        text: 'Optionally have Email Ferret create a task in your Google Tasks "Follow Up" list when an urgent Important email arrives. Requires granting Tasks permission, and you can tune how sensitive it is.',
      },
    ],
  },
  {
    id: 'best-practices',
    title: 'Best practices',
    summary: 'Get the most accurate filtering with the least effort.',
    keywords: ['tips', 'recommendations', 'accuracy', 'setup'],
    related: ['training-from-gmail', 'rules', 'settings'],
    blocks: [
      {
        type: 'steps',
        items: [
          'Connect Gmail and let Email Ferret run for a day or two before judging accuracy.',
          'Review the Messages page and correct any misses — the fastest way is to add/remove the spam label right in Gmail.',
          'Allowlist the domains of clients and key services (*@yourclient.com) so they are never touched.',
          'Tune the threshold: lower it if spam is slipping through, raise it if legitimate mail is being flagged.',
          'Enable a few folders that match how you already organize, then add custom folders as needed.',
        ],
      },
      {
        type: 'callout',
        variant: 'tip',
        title: 'Train as you go',
        text: 'You do not need a big setup session. Just correct mistakes in Gmail as you notice them and the filter sharpens over time.',
      },
    ],
  },
  {
    id: 'faq',
    title: 'FAQ',
    summary: 'Quick answers to common questions.',
    keywords: ['questions', 'help', 'delete', 'safe', 'multiple accounts'],
    related: ['getting-started', 'training-from-gmail'],
    blocks: [
      {
        type: 'heading',
        text: 'Does Email Ferret delete my email?',
      },
      {
        type: 'paragraph',
        text: 'No. Flagged email is labeled and moved out of the way, never deleted. You can always find it under the spam label.',
      },
      {
        type: 'heading',
        text: 'Will it flag people I email with?',
      },
      {
        type: 'paragraph',
        text: 'People you have corresponded with are treated as trusted contacts and are very unlikely to be flagged. You can also allowlist anyone explicitly.',
      },
      {
        type: 'heading',
        text: 'I marked something wrong — how do I fix it?',
      },
      {
        type: 'paragraph',
        text: 'Remove the "Ignore" label in Gmail (or use the allowlist on the Messages page). That sender will be trusted from then on.',
      },
      {
        type: 'heading',
        text: 'Can I use multiple Gmail accounts?',
      },
      {
        type: 'paragraph',
        text: 'Yes. Connect additional accounts from the dashboard. Settings and rules are kept separate per account.',
      },
      {
        type: 'heading',
        text: 'Still stuck?',
      },
      {
        type: 'paragraph',
        text: 'Use the chat bubble in the corner, or reply to any email from us — we are happy to help.',
      },
    ],
  },
];

/** Map for O(1) topic lookup by id. */
export const HELP_TOPIC_MAP: Record<string, HelpTopic> = HELP_TOPICS.reduce(
  (acc, topic) => {
    acc[topic.id] = topic;
    return acc;
  },
  {} as Record<string, HelpTopic>
);

/** Get a topic by id, or undefined if it does not exist. */
export function getHelpTopic(id: string | null | undefined): HelpTopic | undefined {
  if (!id) return undefined;
  return HELP_TOPIC_MAP[id];
}

/**
 * Build the lower-cased searchable text for a topic (title, summary, keywords,
 * and all block text). Used by the drawer's search box.
 */
function topicSearchText(topic: HelpTopic): string {
  const parts: string[] = [topic.title, topic.summary, ...(topic.keywords ?? [])];
  for (const block of topic.blocks) {
    if ('text' in block && block.text) parts.push(block.text);
    if (block.type === 'list' || block.type === 'steps') parts.push(block.items.join(' '));
    if (block.type === 'callout' && block.title) parts.push(block.title);
  }
  return parts.join(' ').toLowerCase();
}

/** Filter topics by a free-text query. Empty query returns all topics. */
export function searchHelpTopics(query: string): HelpTopic[] {
  const q = query.trim().toLowerCase();
  if (!q) return HELP_TOPICS;
  const terms = q.split(/\s+/);
  return HELP_TOPICS.filter((topic) => {
    const haystack = topicSearchText(topic);
    return terms.every((term) => haystack.includes(term));
  });
}
